// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const ROLES = [
  'HOSPITAL_ADMIN',
  'SONOGRAPHER',
  'RADIOLOGIST',
  'PHYSICIAN',
  'ADMIN',
  'SUPER_ADMIN'
];

const DESIGNATIONS = [
  'RELEASING_RADIOLOGIST',
  'CONSULTANT_RADIOLOGIST',
  'SONOGRAPHER',
  'PHYSICIAN_SPECIALIST',
  'CARDIOLOGIST'
];

// Helper function to clean up raw HTML errors from the backend
const sanitizeErrorMessage = (errMessage) => {
  if (!errMessage) return 'An unknown error occurred.';
  if (errMessage.includes('404') || errMessage.includes('<!doctype html>')) {
    return 'API Endpoint not found (404). Please ensure the backend server is running and the route exists.';
  }
  return errMessage;
};

const AdminDashboard = ({ userProfile }) => {
  const [activeSubTab, setActiveSubTab] = useState(
    () => localStorage.getItem('adminDashboardTab') || 'stats'
  );

  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    lockedUsers: 0,
    totalReports: 0,
    reportsToday: 0,
    totalAuditLogs: 0,
    activityToday: 0,
    failedActionsToday: 0
  });

  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Audit log filters
  const [auditFilters, setAuditFilters] = useState({
    action: '',
    userEmail: '',
    success: '',
    page: 0,
    size: 50
  });

  // New user form state
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    serviceNumber: '',
    role: 'SONOGRAPHER',
    designation: 'SONOGRAPHER',
    canUploadSignature: false
  });

  useEffect(() => {
    localStorage.setItem('adminDashboardTab', activeSubTab);
  }, [activeSubTab]);

  // -----------------------------------------------------------
  // Fetch data — runs on tab change, role filter change, or
  // audit filter change. Search query is applied only when
  // the user submits the form (see handleSearch).
  // -----------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      if (activeSubTab === 'stats') {
        const res = await api.getDashboardStats();
        setStats(res?.data || res || {});
      } else if (activeSubTab === 'users') {
        const res = await api.getAllUsers({
          search: searchQuery,
          role: roleFilter
        });
        const userList =
          res?.data?.content ||
          res?.data ||
          (Array.isArray(res) ? res : []);
        setUsers(Array.isArray(userList) ? userList : []);
      } else if (activeSubTab === 'audit') {
        const res = await api.getAuditLogs(auditFilters);
        const logsList =
          res?.data?.content ||
          res?.data ||
          (Array.isArray(res) ? res : []);
        setLogs(Array.isArray(logsList) ? logsList : []);
      }
    } catch (error) {
      const clean = sanitizeErrorMessage(error.message);
      setErrorMsg(clean);
      // Suppress massive HTML console logs for 404s
      if (
        !error.message.includes('404') &&
        !error.message.includes('<!doctype html>')
      ) {
        console.error('Admin dashboard fetch error:', error.message);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, roleFilter, auditFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    // refetch with cleared filters on next tick
    setTimeout(() => fetchData(), 0);
  };

  const handleLockToggle = async (user) => {
    try {
      if (user.accountLocked) {
        await api.unlockUser(user.id);
      } else {
        await api.lockUser(user.id);
      }
      setUsers(
        users.map((u) =>
          u.id === user.id ? { ...u, accountLocked: !u.accountLocked } : u
        )
      );
    } catch (err) {
      alert('Failed to update user lock status: ' + sanitizeErrorMessage(err.message));
    }
  };

  const handleSignatureToggle = async (user) => {
    try {
      const updatedPerm = !user.canUploadSignature;
      await api.updateSignaturePermission(user.id, updatedPerm);
      setUsers(
        users.map((u) =>
          u.id === user.id ? { ...u, canUploadSignature: updatedPerm } : u
        )
      );
    } catch (err) {
      alert('Failed to update signature permission: ' + sanitizeErrorMessage(err.message));
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      setUsers(users.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
      alert('Role updated successfully!');
    } catch (err) {
      alert('Failed to change user role: ' + sanitizeErrorMessage(err.message));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Build payload — omit empty optional fields
      const payload = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        designation: newUser.designation,
        canUploadSignature: !!newUser.canUploadSignature
      };
      if (newUser.username) payload.username = newUser.username;
      if (newUser.phone) payload.phone = newUser.phone;
      if (newUser.department) payload.department = newUser.department;
      if (newUser.serviceNumber) payload.serviceNumber = newUser.serviceNumber;

      await api.createUser(payload);
      alert('User created successfully!');
      setIsCreateModalOpen(false);
      setNewUser({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        phone: '',
        department: '',
        serviceNumber: '',
        role: 'SONOGRAPHER',
        designation: 'SONOGRAPHER',
        canUploadSignature: false
      });
      fetchData();
    } catch (err) {
      alert('Error creating user: ' + sanitizeErrorMessage(err.message));
    }
  };

  // -----------------------------------------------------------
  // Simple role guard — only ADMIN and SUPER_ADMIN should see
  // this dashboard. If userProfile isn't passed, we allow render
  // but the backend will return 403 on every call.
  // -----------------------------------------------------------
  const isAdmin =
    !userProfile ||
    ['ADMIN', 'SUPER_ADMIN', 'HOSPITAL_ADMIN'].includes(userProfile.role);

  if (!isAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        <h2 style={{ color: '#ef4444' }}>Access Denied</h2>
        <p>You do not have permission to view the Admin Dashboard.</p>
      </div>
    );
  }

  return (
    <div
      className="view-container fade-in"
      style={{ padding: '25px', fontFamily: "'Inter', sans-serif" }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div>
          <h2
            style={{
              margin: '0 0 5px 0',
              color: '#0f172a',
              fontSize: '1.6rem',
              fontWeight: '800'
            }}
          >
            Admin Dashboard
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            Manage users, roles, permissions, and system audits.
          </p>
        </div>

        {activeSubTab === 'users' && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            + Create User
          </button>
        )}
      </div>

      {/* Subtabs Header */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '25px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '10px'
        }}
      >
        <button
          onClick={() => setActiveSubTab('stats')}
          style={{
            ...subTabBtnStyle,
            borderBottom:
              activeSubTab === 'stats'
                ? '2px solid #2563eb'
                : '2px solid transparent',
            color: activeSubTab === 'stats' ? '#2563eb' : '#64748b'
          }}
        >
          Statistics
        </button>
        <button
          onClick={() => setActiveSubTab('users')}
          style={{
            ...subTabBtnStyle,
            borderBottom:
              activeSubTab === 'users'
                ? '2px solid #2563eb'
                : '2px solid transparent',
            color: activeSubTab === 'users' ? '#2563eb' : '#64748b'
          }}
        >
          Users Management
        </button>
        <button
          onClick={() => setActiveSubTab('audit')}
          style={{
            ...subTabBtnStyle,
            borderBottom:
              activeSubTab === 'audit'
                ? '2px solid #2563eb'
                : '2px solid transparent',
            color: activeSubTab === 'audit' ? '#2563eb' : '#64748b'
          }}
        >
          Audit Logs
        </button>
      </div>

      {errorMsg && (
        <div
          style={{
            background: '#fef2f2',
            color: '#ef4444',
            padding: '12px 16px',
            borderRadius: '6px',
            border: '1px solid #f87171',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}
        >
          {errorMsg}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Loading dashboard data...
        </div>
      )}

      {/* --- 1. STATS TAB --- */}
      {!loading && activeSubTab === 'stats' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '20px'
          }}
        >
          <div style={statCardStyle}>
            <span style={statLabelStyle}>Total Users</span>
            <h3 style={statValStyle}>{stats.totalUsers || 0}</h3>
            <small style={{ color: '#16a34a', fontWeight: '600' }}>
              {stats.verifiedUsers || 0} Verified
            </small>
          </div>
          <div style={statCardStyle}>
            <span style={statLabelStyle}>Total Reports Generated</span>
            <h3 style={statValStyle}>{stats.totalReports || 0}</h3>
            <small style={{ color: '#2563eb' }}>
              {stats.reportsToday || 0} Created Today
            </small>
          </div>
          <div style={statCardStyle}>
            <span style={statLabelStyle}>Locked Accounts</span>
            <h3
              style={{
                ...statValStyle,
                color: (stats.lockedUsers || 0) > 0 ? '#ef4444' : '#0f172a'
              }}
            >
              {stats.lockedUsers || 0}
            </h3>
            <small style={{ color: '#64748b' }}>Accounts restricted</small>
          </div>
          <div style={statCardStyle}>
            <span style={statLabelStyle}>Total Audit Logs</span>
            <h3 style={statValStyle}>{stats.totalAuditLogs || 0}</h3>
            <small style={{ color: '#64748b' }}>
              {stats.activityToday || 0} logged today
            </small>
          </div>
          <div style={statCardStyle}>
            <span style={statLabelStyle}>Failed Actions Today</span>
            <h3
              style={{
                ...statValStyle,
                color:
                  (stats.failedActionsToday || 0) > 0 ? '#ef4444' : '#0f172a'
              }}
            >
              {stats.failedActionsToday || 0}
            </h3>
            <small style={{ color: '#64748b' }}>Errors &amp; rejects</small>
          </div>
        </div>
      )}

      {/* --- 2. USERS TAB --- */}
      {!loading && activeSubTab === 'users' && (
        <div>
          {/* Search and Filters */}
          <form
            onSubmit={handleSearch}
            style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}
          >
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: '220px',
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                outline: 'none'
              }}
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                outline: 'none',
                background: 'white'
              }}
            >
              <option value="">All Roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Filter
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              style={{
                padding: '10px 20px',
                background: '#e2e8f0',
                color: '#0f172a',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </form>

          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              overflow: 'auto'
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
                minWidth: '720px'
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f8fafc',
                    textAlign: 'left',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#475569'
                  }}
                >
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Signature Allowed</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>
                      <strong>
                        {u.firstName} {u.lastName}
                      </strong>
                      <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {u.email}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem'
                        }}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={u.canUploadSignature || false}
                        onChange={() => handleSignatureToggle(u)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          background: u.accountLocked ? '#fef2f2' : '#f0fdf4',
                          color: u.accountLocked ? '#ef4444' : '#16a34a'
                        }}
                      >
                        {u.accountLocked ? 'LOCKED' : 'ACTIVE'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => setSelectedUser(u)}
                        style={actionBtnLink}
                      >
                        View Profile
                      </button>
                      <span style={{ margin: '0 6px', color: '#cbd5e1' }}>|</span>
                      <button
                        onClick={() => handleLockToggle(u)}
                        style={{
                          ...actionBtnLink,
                          color: u.accountLocked ? '#16a34a' : '#ef4444'
                        }}
                      >
                        {u.accountLocked ? 'Unlock' : 'Lock'}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#94a3b8'
                      }}
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- 3. AUDIT LOGS TAB --- */}
      {!loading && activeSubTab === 'audit' && (
        <div>
          {/* Audit filters */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}
          >
            <input
              type="text"
              placeholder="Filter by action..."
              value={auditFilters.action}
              onChange={(e) =>
                setAuditFilters((f) => ({ ...f, action: e.target.value, page: 0 }))
              }
              style={{
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                minWidth: '200px'
              }}
            />
            <input
              type="text"
              placeholder="Filter by user email..."
              value={auditFilters.userEmail}
              onChange={(e) =>
                setAuditFilters((f) => ({
                  ...f,
                  userEmail: e.target.value,
                  page: 0
                }))
              }
              style={{
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                minWidth: '220px'
              }}
            />
            <select
              value={auditFilters.success}
              onChange={(e) =>
                setAuditFilters((f) => ({ ...f, success: e.target.value, page: 0 }))
              }
              style={{
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px'
              }}
            >
              <option value="">All Outcomes</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
            <button
              onClick={fetchData}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Apply
            </button>
            <button
              onClick={() =>
                setAuditFilters({
                  action: '',
                  userEmail: '',
                  success: '',
                  page: 0,
                  size: 50
                })
              }
              style={{
                padding: '8px 16px',
                background: '#e2e8f0',
                color: '#0f172a',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Reset
            </button>
          </div>

          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              overflow: 'auto'
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                minWidth: '820px'
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f8fafc',
                    textAlign: 'left',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#475569'
                  }}
                >
                  <th style={thStyle}>Timestamp</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>IP Address</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td style={tdStyle}>
                      <strong>{log.userEmail || log.userId}</strong>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td style={tdStyle}>{log.ipAddress || '—'}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          color: log.success ? '#16a34a' : '#ef4444',
                          fontWeight: 'bold'
                        }}
                      >
                        {log.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: '#64748b' }}>
                      {log.details || log.errorMessage || '—'}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#94a3b8'
                      }}
                    >
                      No audit logs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USER DETAILS MODAL */}
      {selectedUser && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3
              style={{
                margin: '0 0 15px 0',
                fontSize: '1.2rem',
                color: '#0f172a'
              }}
            >
              User Details
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                fontSize: '0.9rem',
                color: '#334155'
              }}
            >
              <p>
                <strong>Name:</strong> {selectedUser.firstName}{' '}
                {selectedUser.lastName}
              </p>
              <p>
                <strong>Email:</strong> {selectedUser.email}
              </p>
              <p>
                <strong>Phone:</strong> {selectedUser.phone || 'N/A'}
              </p>
              <p>
                <strong>Role:</strong> {selectedUser.role}
              </p>
              <p>
                <strong>Designation:</strong>{' '}
                {selectedUser.designation || 'N/A'}
              </p>
              <p>
                <strong>Hospital:</strong>{' '}
                {selectedUser.hospitalName || 'N/A'}
              </p>
              <p>
                <strong>Department:</strong>{' '}
                {selectedUser.department || 'N/A'}
              </p>
              <p>
                <strong>Service No:</strong>{' '}
                {selectedUser.serviceNumber || 'N/A'}
              </p>
              <p>
                <strong>Email Verified:</strong>{' '}
                {selectedUser.emailVerified ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Signature Allowed:</strong>{' '}
                {selectedUser.canUploadSignature ? 'Yes' : 'No'}
              </p>
            </div>
            <button
              onClick={() => setSelectedUser(null)}
              style={{
                marginTop: '20px',
                padding: '8px 20px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* CREATE USER MODAL */}
      {isCreateModalOpen && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: '520px' }}>
            <h3
              style={{
                margin: '0 0 15px 0',
                fontSize: '1.2rem',
                color: '#0f172a'
              }}
            >
              Create User Account
            </h3>
            <form
              onSubmit={handleCreateUser}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}
            >
              <div>
                <label style={labelStyle}>First Name *</label>
                <input
                  required
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, firstName: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input
                  required
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, lastName: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Email Address *</label>
                <input
                  required
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Temporary Password *</label>
                <input
                  required
                  type="password"
                  placeholder="Min 8 chars, uppercase, number & symbol"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="text"
                  placeholder="+1234567890"
                  value={newUser.phone}
                  onChange={(e) =>
                    setNewUser({ ...newUser, phone: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <input
                  type="text"
                  value={newUser.department}
                  onChange={(e) =>
                    setNewUser({ ...newUser, department: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value })
                  }
                  style={inputStyle}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Designation *</label>
                <select
                  value={newUser.designation}
                  onChange={(e) =>
                    setNewUser({ ...newUser, designation: e.target.value })
                  }
                  style={inputStyle}
                >
                  {DESIGNATIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Service Number</label>
                <input
                  type="text"
                  value={newUser.serviceNumber}
                  onChange={(e) =>
                    setNewUser({ ...newUser, serviceNumber: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div
                style={{
                  gridColumn: 'span 2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '5px 0'
                }}
              >
                <input
                  type="checkbox"
                  id="canSig"
                  checked={newUser.canUploadSignature}
                  onChange={(e) =>
                    setNewUser({
                      ...newUser,
                      canUploadSignature: e.target.checked
                    })
                  }
                />
                <label
                  htmlFor="canSig"
                  style={{ fontSize: '0.85rem', color: '#334155' }}
                >
                  Grant Permission to Upload Digital Signature
                </label>
              </div>

              <div
                style={{
                  gridColumn: 'span 2',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  marginTop: '10px'
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 20px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const subTabBtnStyle = {
  background: 'none',
  border: 'none',
  padding: '10px 18px',
  fontSize: '0.95rem',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s'
};
const statCardStyle = {
  padding: '20px',
  borderRadius: '12px',
  background: 'white',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
};
const statLabelStyle = {
  fontSize: '0.85rem',
  color: '#64748b',
  fontWeight: '600',
  textTransform: 'uppercase'
};
const statValStyle = {
  fontSize: '1.8rem',
  fontWeight: '800',
  color: '#0f172a',
  margin: '8px 0 4px 0'
};
const thStyle = { padding: '12px 16px', fontWeight: '600' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };
const actionBtnLink = {
  background: 'none',
  border: 'none',
  color: '#2563eb',
  cursor: 'pointer',
  fontWeight: '600',
  padding: 0
};
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2000
};
const modalContentStyle = {
  background: 'white',
  padding: '30px',
  borderRadius: '12px',
  width: '450px',
  maxWidth: '90%',
  maxHeight: '90vh',
  overflowY: 'auto'
};
const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '4px'
};
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  fontSize: '0.85rem',
  boxSizing: 'border-box'
};

export default AdminDashboard;